// Modules × apps: which app runs which module, and how current its version is.
// Each cell is the worst lag among that app's calls of the module.

import React, { useMemo, useState } from 'react';
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Link as MuiLink,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { GridOn as MatrixIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { useFleetModules } from '../../hooks/useFleetModules';
import { getColors, rounded } from '../../theme/tokens';
import { buildAdoptionMatrix, LAG_ORDER, modulePath } from '../../lib/fleetModules';
import { FleetLoadNote, LAG_HUES, LAG_LABELS, Legend } from '../../components/infra/charts';

const CELL = 18;

const AdoptionPage: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const hues = LAG_HUES[mode];
  const { fleet, progress, loading, registry } = useFleetModules();
  const [directOnly, setDirectOnly] = useState(true);
  const [query, setQuery] = useState('');

  const matrix = useMemo(() => buildAdoptionMatrix(fleet, { directOnly }), [fleet, directOnly]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? matrix.rows.filter(r => r.module.source.toLowerCase().includes(q)) : matrix.rows;
  }, [matrix.rows, query]);

  return (
    <Box>
      <PageHeader
        title="Adoption"
        crumbs={[{ label: 'Infrastructure', to: '/infra' }, { label: 'Adoption' }]}
        subtitle="Which apps run which modules. Each cell shows the furthest-behind version that app has deployed."
        actions={
          <>
            <FormControlLabel
              control={<Switch size="small" checked={directOnly} onChange={e => setDirectOnly(e.target.checked)} />}
              label={<Typography variant="body2">Direct calls only</Typography>}
            />
            <TextField
              size="small"
              placeholder="Filter modules"
              value={query}
              onChange={e => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </>
        }
      />
      <FleetLoadNote loaded={progress.loaded} total={progress.total} loading={loading} registry={registry} />

      {!loading && rows.length === 0 ? (
        <EmptyState icon={<MatrixIcon />} title="Nothing to show" body="No module matches, or no job has recorded its modules." />
      ) : (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${rounded.lg}px`, borderColor: c.hairline }}>
          <Legend
            items={LAG_ORDER.filter(l => l !== 'unknown').map(l => ({ label: LAG_LABELS[l], color: hues[l] }))}
          />
          <Box sx={{ overflow: 'auto', mt: 2, maxHeight: 'calc(100vh - 280px)' }}>
            <Box
              component="table"
              sx={{ borderCollapse: 'separate', borderSpacing: '3px', '& th, & td': { p: 0 } }}
            >
              <thead>
                <tr>
                  <Box component="th" sx={{ position: 'sticky', left: 0, top: 0, zIndex: 3, backgroundColor: c.surface }} />
                  {matrix.apps.map(app => (
                    <Box
                      component="th"
                      key={app}
                      sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: c.surface, verticalAlign: 'bottom', height: 140 }}
                    >
                      <Typography
                        sx={{
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          fontSize: '0.75rem',
                          color: c.inkSecondary,
                          whiteSpace: 'nowrap',
                          mx: 'auto',
                          pb: 0.5,
                        }}
                      >
                        {app}
                      </Typography>
                    </Box>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ module, cells }) => (
                  <tr key={module.source}>
                    <Box
                      component="th"
                      sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: c.surface, textAlign: 'left', pr: 1.5 }}
                    >
                      <MuiLink
                        component={RouterLink}
                        to={modulePath(module.source)}
                        underline="hover"
                        title={module.source}
                        sx={{ fontSize: '0.8rem', fontWeight: 500, color: c.ink, whiteSpace: 'nowrap' }}
                      >
                        {module.name}
                      </MuiLink>
                    </Box>
                    {matrix.apps.map(app => {
                      const cell = cells[app];
                      return (
                        <td key={app}>
                          {cell ? (
                            <Tooltip
                              arrow
                              title={
                                <span>
                                  <b>{app}</b> · {module.name}
                                  <br />
                                  {cell.versions.join(', ')} ({LAG_LABELS[cell.lag].toLowerCase()})
                                  <br />
                                  {cell.usages} call{cell.usages === 1 ? '' : 's'}
                                </span>
                              }
                            >
                              <Box
                                component={RouterLink}
                                to={modulePath(module.source)}
                                aria-label={`${app} ${module.name} ${cell.versions.join(', ')}`}
                                sx={{
                                  display: 'block',
                                  width: CELL,
                                  height: CELL,
                                  borderRadius: '4px',
                                  backgroundColor: hues[cell.lag],
                                  '&:hover, &:focus-visible': { outline: `2px solid ${c.ink}`, outlineOffset: 1 },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            <Box sx={{ width: CELL, height: CELL, borderRadius: '4px', backgroundColor: c.hoverWash }} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AdoptionPage;
